import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Surface, formatINR } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Wallet, RotateCcw, Download } from "lucide-react";
import { toast } from "sonner";
import { csvCell, downloadCSV } from "@/pages/WorkerLedger";
import { formatDateTime } from "@/lib/datetime";

const fmt = (n) => formatINR(n ?? 0);
const fmtDT = (iso) => formatDateTime(iso) || "—";
const asNum = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };

export default function WorkerLedgerDetail() {
  const { workerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ worker: null, items: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [entryType, setEntryType] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (entryType !== "all") params.entry_type = entryType;
      const r = await api.get(`/production/workers/${workerId}/ledger`, { params });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  }, [workerId, dateFrom, dateTo, entryType]);
  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    if (!data.worker) return;
    const header = ["Date", "Type", "Reference", "Amount", "Balance After", "Notes"];
    const lines = [header.join(",")];
    // Chronological (oldest first) for accounting readability
    const rows = [...(data.items || [])].reverse();
    for (const e of rows) {
      const signedAmount = e.entry_type === "earned" ? e.amount : -e.amount;
      lines.push([
        csvCell(e.created_at || ""),
        csvCell((e.entry_type || "").replace("_", " ")),
        csvCell(e.ref_no || ""),
        signedAmount,
        e.balance_after ?? 0,
        csvCell(e.notes || ""),
      ].join(","));
    }
    // Append a summary block for context
    lines.push("");
    lines.push(`Worker,${csvCell(data.worker.name)}`);
    lines.push(`Mobile,${csvCell(data.worker.mobile || "")}`);
    lines.push(`Type,${csvCell(data.worker.worker_type || "")}`);
    lines.push(`Total Earned,${data.summary.total_earned ?? 0}`);
    lines.push(`Total Paid,${data.summary.total_paid ?? 0}`);
    lines.push(`Total Reversed,${data.summary.total_reversed ?? 0}`);
    lines.push(`Current Balance,${data.summary.current_balance ?? 0}`);
    downloadCSV(lines.join("\n"), `worker-ledger-${(data.worker.name || "worker").replace(/[^a-z0-9]+/gi, "-")}-${new Date().toISOString().slice(0, 10)}.csv`);
    toast.success(`Exported ${rows.length} entries`);
  };

  if (loading && !data.worker) return <div className="p-8 text-gray-500">Loading…</div>;
  if (!data.worker) return <div className="p-8 text-red-600">Worker not found</div>;

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => navigate("/production/worker-ledger")} className="gap-1.5" data-testid="wld-back">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <div className="flex-1">
          <h2 className="font-heading font-bold text-xl text-gray-900" data-testid="wld-worker-name">{data.worker.name}</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium mr-2 ${data.worker.worker_type === "Commission" ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"}`}>{data.worker.worker_type || "—"}</span>
            {data.worker.mobile ? <span className="font-mono">{data.worker.mobile}</span> : null}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCSV} className="gap-1.5" data-testid="wld-export-csv">
          <Download className="h-4 w-4" /> Export CSV
        </Button>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" onClick={() => setPaying(true)} data-testid="wld-record-payment">
          <Wallet className="h-4 w-4" /> Record Payment
        </Button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-4 gap-3">
        <Tile label="Total Earned" value={fmt(data.summary.total_earned)} tone="emerald" testid="wld-tile-earned" />
        <Tile label="Total Paid" value={fmt(data.summary.total_paid)} tone="blue" testid="wld-tile-paid" />
        <Tile label="Reversed" value={fmt(data.summary.total_reversed)} tone="gray" testid="wld-tile-reversed" />
        <Tile label="Current Balance" value={fmt(data.summary.current_balance)} tone={data.summary.current_balance > 0 ? "red" : "amber"} highlight testid="wld-tile-balance" />
      </div>

      {/* Ledger table */}
      <Surface padded={false}>
        <div className="p-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40 text-sm" title="From date" data-testid="wld-date-from" />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40 text-sm" title="To date" data-testid="wld-date-to" />
          <select value={entryType} onChange={(e) => setEntryType(e.target.value)} className="h-9 rounded-md border border-gray-200 text-sm px-2" data-testid="wld-entry-type-filter">
            <option value="all">All Entries</option>
            <option value="earned">Earned</option>
            <option value="paid">Paid</option>
            <option value="earned_reversal">Reversed</option>
          </select>
          {(dateFrom || dateTo || entryType !== "all") && (
            <button onClick={() => { setDateFrom(""); setDateTo(""); setEntryType("all"); }} className="text-[11px] text-gray-500 hover:text-gray-900 underline" data-testid="wld-clear-filters">Clear</button>
          )}
          <span className="ml-auto text-xs text-gray-500 tabular-nums">{data.items.length} entries</span>
        </div>
        <div className="overflow-auto max-h-[calc(100vh-360px)]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2.5 w-40">Date</th>
                <th className="px-3 py-2.5 w-40">Reference</th>
                <th className="px-3 py-2.5">Particulars</th>
                <th className="px-3 py-2.5 text-right w-28 text-emerald-700">Credit (Earned)</th>
                <th className="px-3 py-2.5 text-right w-28 text-blue-700">Debit (Paid)</th>
                <th className="px-3 py-2.5 text-right w-32 bg-amber-50/40 text-amber-900">Running Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.items.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-gray-400">No entries yet</td></tr>}
              {data.items.map((e) => {
                const isEarn = e.entry_type === "earned";
                const isPaid = e.entry_type === "paid";
                const isReversal = e.entry_type === "earned_reversal";
                const credit = isEarn ? Number(e.amount || 0) : 0;
                const debit = isPaid || isReversal ? Number(e.amount || 0) : 0;
                return (
                  <tr key={e.id} className="hover:bg-gray-50" data-testid={`wld-row-${e.id}`}>
                    <td className="px-3 py-2 text-xs text-gray-600 tabular-nums">{fmtDT(e.created_at)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-700">{e.ref_no || "—"}</td>
                    <td className="px-3 py-2 text-xs text-gray-600 max-w-md">
                      <div className="flex items-center gap-2">
                        {isReversal && <RotateCcw className="h-3 w-3 text-gray-400 shrink-0" />}
                        <span>{e.notes || "—"}</span>
                      </div>
                      {e.source && <div className="text-[10px] text-gray-400 uppercase tracking-wider mt-0.5">{e.source.replace(/_/g, " ")}</div>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs tabular-nums text-emerald-700 font-semibold">
                      {credit > 0 ? fmt(credit) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs tabular-nums text-blue-700 font-semibold">
                      {debit > 0 ? fmt(debit) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className={`px-3 py-2 text-right font-mono text-xs tabular-nums font-bold bg-amber-50/40 ${
                      Number(e.balance_after) < 0 ? "text-red-700" : "text-amber-900"
                    }`}>{fmt(e.balance_after)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Surface>

      {paying && <PaymentModal worker={{ worker_id: data.worker.id, worker_name: data.worker.name }} onClose={() => setPaying(false)} onSaved={() => { setPaying(false); load(); }} />}
    </div>
  );
}

function Tile({ label, value, tone, highlight, testid }) {
  const tones = {
    emerald: "text-emerald-700",
    blue: "text-blue-700",
    gray: "text-gray-600",
    red: "text-red-700",
    amber: "text-amber-700",
  };
  return (
    <div className={`rounded-lg border ${highlight ? "border-gray-300 bg-white shadow-sm" : "border-gray-200 bg-white"} p-3`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">{label}</div>
      <div className={`mt-1 text-xl font-heading font-bold tabular-nums ${tones[tone] || "text-gray-900"}`}>{value}</div>
    </div>
  );
}

function PaymentModal({ worker, onClose, onSaved }) {
  const [form, setForm] = useState({
    payment_date: new Date().toISOString().slice(0, 10),
    amount: "",
    payment_mode: "Cash",
    reference_no: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (asNum(form.amount) <= 0) { toast.error("Amount must be > 0"); return; }
    setSaving(true);
    try {
      const r = await api.post("/production/worker-payments", {
        payment_date: form.payment_date,
        worker_id: worker.worker_id,
        amount: asNum(form.amount),
        payment_mode: form.payment_mode,
        reference_no: form.reference_no.trim() || null,
        notes: form.notes.trim() || null,
      });
      toast.success(`${r.data.payment_no} recorded · balance: ${fmt(r.data.balance_after)}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="wld-pay-dialog">
        <DialogHeader>
          <DialogTitle>Pay {worker.worker_name}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <F label="Date"><Input type="date" value={form.payment_date} onChange={(e) => set("payment_date", e.target.value)} data-testid="wld-pay-date" /></F>
          <F label="Amount (₹)"><Input type="number" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="text-right tabular-nums" data-testid="wld-pay-amount" autoFocus /></F>
          <F label="Mode">
            <Select value={form.payment_mode} onValueChange={(v) => set("payment_mode", v)}>
              <SelectTrigger data-testid="wld-pay-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Cash">Cash</SelectItem>
                <SelectItem value="UPI">UPI</SelectItem>
                <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                <SelectItem value="Cheque">Cheque</SelectItem>
              </SelectContent>
            </Select>
          </F>
          <F label="Reference #"><Input value={form.reference_no} onChange={(e) => set("reference_no", e.target.value)} data-testid="wld-pay-ref" /></F>
          <F label="Notes" className="col-span-2"><Input value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="wld-pay-notes" /></F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={save} disabled={saving} data-testid="wld-pay-save">
            {saving ? "Saving…" : "Save Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, className = "", children }) {
  return <label className={`text-sm ${className}`}><span className="block text-[11px] uppercase tracking-widest text-gray-500 mb-1">{label}</span>{children}</label>;
}
